import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import type { TenantUser, UserRole } from '@atomfit/shared';
import { adminApi, type FranchiseTenant } from '../../api/admin.api.js';
import { settingsApi, type StripeConnectStatus } from '../../api/settings.api.js';
import { usersApi } from '../../api/users.api.js';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { CustomSelect, Spinner, Tag } from '../../components/design/AtomFitUI.js';
import { useAuth } from '../../hooks/useAuth.js';

type ModalState = { type: 'none' } | { type: 'create' } | { type: 'edit'; tenant: FranchiseTenant };

interface TenantForm {
  name: string;
  slug: string;
  ownerEmail: string;
  ownerFirstName: string;
  ownerLastName: string;
  defaultCurrency: string;
  revenueSharePct: string;
  taxId: string;
  isActive: boolean;
}

const emptyForm: TenantForm = {
  name: '',
  slug: '',
  ownerEmail: '',
  ownerFirstName: '',
  ownerLastName: '',
  defaultCurrency: 'CZK',
  revenueSharePct: '8',
  taxId: '',
  isActive: true,
};

function toSlug(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function tenantToForm(tenant: FranchiseTenant): TenantForm {
  return {
    name: tenant.name,
    slug: tenant.slug,
    ownerEmail: '',
    ownerFirstName: '',
    ownerLastName: '',
    defaultCurrency: tenant.settings?.defaultCurrency ?? 'CZK',
    revenueSharePct: String(tenant.settings?.revenueSharePct ?? 8),
    taxId: tenant.settings?.taxId ?? '',
    isActive: tenant.isActive,
  };
}

function stripeTag(tenant: FranchiseTenant) {
  if (tenant.settings?.stripeChargesEnabled && tenant.settings.stripePayoutsEnabled) {
    return (
      <Tag color="var(--af-success)" bg="rgba(74,222,128,0.1)">
        {translate('pages.admin.admin_franchise_page.stripe_tag.tag.copy')}
      </Tag>
    );
  }
  if (tenant.settings?.stripeAccountId) {
    return (
      <Tag color="var(--af-peak)" bg="var(--af-peak-soft)">
        {translate('pages.admin.admin_franchise_page.stripe_tag.tag.copy_2')}
      </Tag>
    );
  }
  return (
    <Tag color="var(--af-dim)" bg="rgba(255,255,255,0.04)">
      {translate('pages.admin.admin_franchise_page.stripe_tag.tag.copy_3')}
    </Tag>
  );
}

function statusTag(active: boolean) {
  return active ? (
    <Tag color="var(--af-success)" bg="rgba(74,222,128,0.1)">
      {translate('pages.admin.admin_franchise_page.status_tag.tag.active')}
    </Tag>
  ) : (
    <Tag color="var(--af-danger)" bg="rgba(232,66,26,0.1)">
      {translate('pages.admin.admin_franchise_page.status_tag.tag.paused')}
    </Tag>
  );
}

export function AdminFranchisePage() {
  const { role } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const [tenants, setTenants] = useState<FranchiseTenant[]>([]);
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [openingStripe, setOpeningStripe] = useState(false);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [form, setForm] = useState<TenantForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<FranchiseTenant | null>(null);

  async function loadTenants() {
    setLoading(true);
    try {
      const res = await adminApi.listTenants(1);
      setTenants(res.data.data);
      setSelectedTenant((current) => {
        if (!current) return null;
        return res.data.data.find((tenant) => tenant.id === current.id) ?? null;
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    if (isPlatform) {
      void loadTenants();
      return;
    }
    settingsApi
      .stripeStatus()
      .then((res) => setStatus(res.data))
      .finally(() => setLoading(false));
  }, [isPlatform]);

  function openCreate() {
    setForm(emptyForm);
    setError(null);
    setModal({ type: 'create' });
  }

  function openEdit(tenant: FranchiseTenant) {
    setForm(tenantToForm(tenant));
    setError(null);
    setModal({ type: 'edit', tenant });
  }

  async function saveTenant(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (modal.type === 'create') {
        await adminApi.createTenant({
          name: form.name,
          slug: form.slug,
          ownerEmail: form.ownerEmail.trim(),
          ownerFirstName: form.ownerFirstName || undefined,
          ownerLastName: form.ownerLastName || undefined,
          defaultCurrency: form.defaultCurrency.toUpperCase(),
          revenueSharePct: Number(form.revenueSharePct),
          taxId: nullableString(form.taxId),
        });
      } else if (modal.type === 'edit') {
        await adminApi.updateTenant(modal.tenant.id, {
          name: form.name,
          slug: form.slug,
          isActive: form.isActive,
          defaultCurrency: form.defaultCurrency.toUpperCase(),
          revenueSharePct: Number(form.revenueSharePct),
          taxId: nullableString(form.taxId),
        });
      }
      setModal({ type: 'none' });
      void loadTenants();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        msg ??
          translate('pages.admin.admin_franchise_page.save_tenant.set_error.error_tenant_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function toggleTenant(tenant: FranchiseTenant) {
    await adminApi.updateTenant(tenant.id, { isActive: !tenant.isActive });
    void loadTenants();
  }

  async function openStripeOnboarding() {
    setOpeningStripe(true);
    try {
      const res = await settingsApi.stripeAccountLink();
      window.location.assign(res.data.url);
    } finally {
      setOpeningStripe(false);
    }
  }

  return (
    <AdminLayout
      title={translate('pages.admin.admin_franchise_page.admin_franchise_page.admin_layout.tenant')}
      eyebrow={
        isPlatform
          ? translate('pages.admin.admin_franchise_page.admin_franchise_page.admin_layout.eyebrow')
          : translate(
              'pages.admin.admin_franchise_page.admin_franchise_page.admin_layout.eyebrow_2',
            )
      }
    >
      {isPlatform ? (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
              {loading
                ? translate('pages.admin.admin_franchise_page.admin_franchise_page.div.loading')
                : translate('pages.admin.admin_franchise_page.admin_franchise_page.div.tenant', {
                    value1: tenants.length,
                  })}
            </div>
            <button
              className="af-primary-btn"
              style={{ padding: '10px 18px', fontSize: 13 }}
              onClick={openCreate}
            >
              {translate(
                'pages.admin.admin_franchise_page.admin_franchise_page.button_primary_btn.tenant_add',
              )}
            </button>
          </div>

          <div className="af-card" style={{ overflow: 'auto' }}>
            {loading ? (
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  padding: 24,
                  color: 'var(--af-muted)',
                }}
              >
                <Spinner />{' '}
                {translate(
                  'pages.admin.admin_franchise_page.admin_franchise_page.div.loading_tenant',
                )}
              </div>
            ) : tenants.length === 0 ? (
              <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--af-muted)' }}>
                {translate('pages.admin.admin_franchise_page.admin_franchise_page.div.tenant_2')}
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
                    {[
                      translate('pages.admin.admin_franchise_page.admin_franchise_page.tr.tenant'),
                      translate('pages.admin.admin_franchise_page.admin_franchise_page.tr.type'),
                      translate('pages.admin.admin_franchise_page.admin_franchise_page.tr.gym'),
                      translate('pages.admin.admin_franchise_page.admin_franchise_page.tr.user'),
                      translate('pages.admin.admin_franchise_page.admin_franchise_page.tr.pricing'),
                      translate('pages.admin.admin_franchise_page.admin_franchise_page.tr.stripe'),
                      translate('pages.admin.admin_franchise_page.admin_franchise_page.tr.status'),

                      '',
                    ].map((heading) => (
                      <th
                        key={heading}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: 10,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: 'var(--af-muted)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((tenant, index) => (
                    <tr
                      key={tenant.id}
                      style={{
                        borderBottom:
                          index < tenants.length - 1 ? '1px solid var(--af-border)' : 'none',
                      }}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 800 }}>{tenant.name}</div>
                        <div
                          className="af-mono"
                          style={{ color: 'var(--af-dim)', fontSize: 11, marginTop: 2 }}
                        >
                          {tenant.slug}
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <Tag
                          color={
                            tenant.type === 'PLATFORM' ? 'var(--af-accent)' : 'var(--af-muted)'
                          }
                          bg={
                            tenant.type === 'PLATFORM'
                              ? 'var(--af-accent-soft)'
                              : 'rgba(255,255,255,0.06)'
                          }
                        >
                          {tenant.type}
                        </Tag>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>
                        {tenant._count.gyms}
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>
                        {tenant._count.memberships}
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--af-muted)' }}>
                        {tenant.settings?.revenueSharePct ?? 8}
                        {'%'}
                      </td>
                      <td style={{ padding: '14px 16px' }}>{stripeTag(tenant)}</td>
                      <td style={{ padding: '14px 16px' }}>{statusTag(tenant.isActive)}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            className="af-outline-btn"
                            style={{ padding: '6px 12px' }}
                            disabled={tenant.type !== 'OWNER'}
                            onClick={() => openEdit(tenant)}
                          >
                            {translate(
                              'pages.admin.admin_franchise_page.admin_franchise_page.button_outline_btn.edit',
                            )}
                          </button>
                          <button
                            className="af-outline-btn"
                            style={{ padding: '6px 12px' }}
                            onClick={() => setSelectedTenant(tenant)}
                          >
                            {translate(
                              'pages.admin.admin_franchise_page.admin_franchise_page.button_outline_btn.admin',
                            )}
                          </button>
                          <button
                            className="af-outline-btn"
                            style={{
                              padding: '6px 12px',
                              color:
                                tenant.type !== 'OWNER'
                                  ? 'var(--af-dim)'
                                  : tenant.isActive
                                    ? 'var(--af-danger)'
                                    : 'var(--af-success)',
                            }}
                            disabled={tenant.type !== 'OWNER'}
                            onClick={() => void toggleTenant(tenant)}
                          >
                            {tenant.isActive
                              ? translate(
                                  'pages.admin.admin_franchise_page.admin_franchise_page.button_outline_btn.status',
                                )
                              : translate(
                                  'pages.admin.admin_franchise_page.admin_franchise_page.button_outline_btn.active',
                                )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {selectedTenant && (
            <Overlay onClose={() => setSelectedTenant(null)}>
              <FranchiseAdminsPanel
                tenant={selectedTenant}
                onClose={() => setSelectedTenant(null)}
                onChanged={() => void loadTenants()}
              />
            </Overlay>
          )}

          {modal.type !== 'none' && (
            <Overlay onClose={() => setModal({ type: 'none' })}>
              <form
                className="af-card"
                style={{ width: '100%', maxWidth: 560, padding: 28 }}
                onSubmit={saveTenant}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 800 }}>
                  {modal.type === 'create'
                    ? translate(
                        'pages.admin.admin_franchise_page.admin_franchise_page.h2.tenant_create',
                      )
                    : translate(
                        'pages.admin.admin_franchise_page.admin_franchise_page.h2.tenant_edit',
                      )}
                </h2>
                <div style={{ display: 'grid', gap: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_franchise_page.admin_franchise_page.label_label.copy',
                      )}

                      <input
                        className="af-input"
                        value={form.name}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            name: e.target.value,
                            slug: modal.type === 'create' ? toSlug(e.target.value) : p.slug,
                          }))
                        }
                        required
                      />
                    </label>
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_franchise_page.admin_franchise_page.label_label.copy_2',
                      )}

                      <input
                        className="af-input"
                        value={form.slug}
                        onChange={(e) => setForm((p) => ({ ...p, slug: toSlug(e.target.value) }))}
                        required
                      />
                    </label>
                  </div>

                  {modal.type === 'create' && (
                    <>
                      <label className="af-label">
                        {translate(
                          'pages.admin.admin_franchise_page.admin_franchise_page.label_label.email',
                        )}

                        <input
                          className="af-input"
                          type="email"
                          value={form.ownerEmail}
                          onChange={(e) => setForm((p) => ({ ...p, ownerEmail: e.target.value }))}
                          required
                        />
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <label className="af-label">
                          {translate(
                            'pages.admin.admin_franchise_page.admin_franchise_page.label_label.name',
                          )}

                          <input
                            className="af-input"
                            value={form.ownerFirstName}
                            onChange={(e) =>
                              setForm((p) => ({ ...p, ownerFirstName: e.target.value }))
                            }
                          />
                        </label>
                        <label className="af-label">
                          {translate(
                            'pages.admin.admin_franchise_page.admin_franchise_page.label_label.last_name',
                          )}

                          <input
                            className="af-input"
                            value={form.ownerLastName}
                            onChange={(e) =>
                              setForm((p) => ({ ...p, ownerLastName: e.target.value }))
                            }
                          />
                        </label>
                      </div>
                    </>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_franchise_page.admin_franchise_page.label_label.currency',
                      )}

                      <input
                        className="af-input"
                        value={form.defaultCurrency}
                        maxLength={3}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, defaultCurrency: e.target.value.toUpperCase() }))
                        }
                      />
                    </label>
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_franchise_page.admin_franchise_page.label_label.pricing',
                      )}

                      <input
                        className="af-input"
                        type="number"
                        min={0}
                        max={100}
                        value={form.revenueSharePct}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, revenueSharePct: e.target.value }))
                        }
                      />
                    </label>
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_franchise_page.admin_franchise_page.label_label.status',
                      )}

                      <CustomSelect
                        value={form.isActive ? 'active' : 'paused'}
                        onChange={(value) =>
                          setForm((p) => ({ ...p, isActive: value === 'active' }))
                        }
                        disabled={modal.type === 'create'}
                        options={[
                          {
                            value: 'active',
                            label: translate(
                              'pages.admin.admin_franchise_page.admin_franchise_page.custom_select.options',
                            ),
                          },
                          {
                            value: 'paused',
                            label: translate(
                              'pages.admin.admin_franchise_page.admin_franchise_page.custom_select.options_2',
                            ),
                          },
                        ]}
                      />
                    </label>
                  </div>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_franchise_page.admin_franchise_page.label_label.copy_3',
                    )}

                    <input
                      className="af-input af-mono"
                      value={form.taxId}
                      onChange={(e) => setForm((p) => ({ ...p, taxId: e.target.value }))}
                    />
                  </label>
                  {modal.type === 'create' && (
                    <div
                      style={{
                        padding: '10px 12px',
                        border: '1px solid var(--af-border)',
                        background: 'var(--af-s2)',
                        borderRadius: 8,
                        color: 'var(--af-muted)',
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                    >
                      {translate(
                        'pages.admin.admin_franchise_page.admin_franchise_page.div.password_email_tenant',
                      )}
                    </div>
                  )}
                  {error && <div className="af-error">{error}</div>}
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}
                >
                  <button
                    className="af-outline-btn"
                    type="button"
                    style={{ padding: '10px 20px' }}
                    onClick={() => setModal({ type: 'none' })}
                    disabled={saving}
                  >
                    {translate(
                      'pages.admin.admin_franchise_page.admin_franchise_page.button_outline_btn.cancel',
                    )}
                  </button>
                  <button
                    className="af-primary-btn"
                    style={{ padding: '10px 22px', fontSize: 14 }}
                    disabled={saving}
                  >
                    {saving ? (
                      <Spinner />
                    ) : (
                      translate(
                        'pages.admin.admin_franchise_page.admin_franchise_page.button_primary_btn.save',
                      )
                    )}
                  </button>
                </div>
              </form>
            </Overlay>
          )}
        </>
      ) : (
        <div className="af-admin-main-grid">
          <section className="af-card" style={{ padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>
              {translate('pages.admin.admin_franchise_page.admin_franchise_page.div.stripe')}
            </div>
            <p
              style={{
                margin: '0 0 18px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {translate('pages.admin.admin_franchise_page.admin_franchise_page.p.stripe')}
            </p>
            {loading ? (
              <div
                style={{ display: 'flex', gap: 10, alignItems: 'center', color: 'var(--af-muted)' }}
              >
                <Spinner />{' '}
                {translate('pages.admin.admin_franchise_page.admin_franchise_page.div.loading_2')}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 10, marginBottom: 18 }}>
                <StatusLine
                  label={translate(
                    'pages.admin.admin_franchise_page.admin_franchise_page.status_line.label',
                  )}
                  ready={Boolean(status?.connected)}
                />

                <StatusLine
                  label={translate(
                    'pages.admin.admin_franchise_page.admin_franchise_page.status_line.payment',
                  )}
                  ready={Boolean(status?.chargesEnabled)}
                />

                <StatusLine
                  label={translate(
                    'pages.admin.admin_franchise_page.admin_franchise_page.status_line.label_2',
                  )}
                  ready={Boolean(status?.payoutsEnabled)}
                />

                <StatusLine
                  label={translate(
                    'pages.admin.admin_franchise_page.admin_franchise_page.status_line.details',
                  )}
                  ready={Boolean(status?.detailsSubmitted)}
                />
              </div>
            )}
            <button
              className="af-primary-btn"
              style={{ padding: '12px 18px', fontSize: 13 }}
              disabled={openingStripe}
              onClick={() => void openStripeOnboarding()}
            >
              {openingStripe ? (
                <Spinner />
              ) : status?.connected ? (
                translate(
                  'pages.admin.admin_franchise_page.admin_franchise_page.button_primary_btn.stripe',
                )
              ) : (
                translate(
                  'pages.admin.admin_franchise_page.admin_franchise_page.button_primary_btn.stripe_2',
                )
              )}
            </button>
          </section>
          <section className="af-card" style={{ padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>
              {translate('pages.admin.admin_franchise_page.admin_franchise_page.div.pricing')}
            </div>
            <div
              style={{ fontSize: 34, fontWeight: 800, color: 'var(--af-accent)', marginBottom: 8 }}
            >
              {'92%'}
            </div>
            <p style={{ margin: 0, color: 'var(--af-muted)', fontSize: 14, lineHeight: 1.6 }}>
              {translate('pages.admin.admin_franchise_page.admin_franchise_page.p.tenant_pricing')}
            </p>
          </section>
        </div>
      )}
    </AdminLayout>
  );
}

function nullableString(value: string) {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

type AdminModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; user: TenantUser }
  | { type: 'remove'; user: TenantUser };

interface AdminForm {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: UserRole;
}

const emptyAdminForm: AdminForm = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  role: 'OWNER_ADMIN',
};

const tenantRoleLabels: Record<UserRole, string> = {
  PLATFORM_ADMIN: translate('pages.admin.admin_franchise_page.tenant_role_labels.admin'),
  OWNER_ADMIN: translate('pages.admin.admin_franchise_page.tenant_role_labels.admin_2'),
  GYM_STAFF: translate('pages.admin.admin_franchise_page.tenant_role_labels.gym_staff'),
};

function tenantAssignableRoles(tenantType: FranchiseTenant['type']): UserRole[] {
  return tenantType === 'PLATFORM' ? ['PLATFORM_ADMIN'] : ['OWNER_ADMIN', 'GYM_STAFF'];
}

function userToAdminForm(user: TenantUser): AdminForm {
  return {
    email: user.account.email,
    firstName: user.account.firstName ?? '',
    lastName: user.account.lastName ?? '',
    phone: user.account.phone ?? '',
    role: user.role === 'CUSTOMER' ? 'GYM_STAFF' : user.role,
  };
}

function FranchiseAdminsPanel({
  tenant,
  onClose,
  onChanged,
}: {
  tenant: FranchiseTenant;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [admins, setAdmins] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<AdminModalState>({ type: 'none' });
  const [form, setForm] = useState<AdminForm>(emptyAdminForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const assignableRoles = tenantAssignableRoles(tenant.type);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const responses = await Promise.all(
        tenantAssignableRoles(tenant.type).map((role) =>
          usersApi.list({ tenantSlug: tenant.slug, role, page: 1 }),
        ),
      );
      setAdmins(
        responses
          .flatMap((response) => response.data.data)
          .sort(
            (a, b) =>
              a.role.localeCompare(b.role) || a.account.email.localeCompare(b.account.email),
          ),
      );
    } finally {
      setLoading(false);
    }
  }, [tenant.slug, tenant.type]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setForm({
      ...emptyAdminForm,
      role: tenant.type === 'PLATFORM' ? 'PLATFORM_ADMIN' : 'OWNER_ADMIN',
    });
    setError(null);
    setModal({ type: 'create' });
    setTimeout(() => emailRef.current?.focus(), 50);
  }

  function openEdit(user: TenantUser) {
    setForm(userToAdminForm(user));
    setError(null);
    setModal({ type: 'edit', user });
  }

  async function saveAdmin(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    if (form.phone.trim() && !isValidPhoneNumber(form.phone)) {
      setError(translate('pages.admin.admin_franchise_page.save_admin.set_error.phone'));
      setSaving(false);
      return;
    }
    try {
      if (modal.type === 'create') {
        await usersApi.create(
          {
            email: form.email.trim(),
            firstName: form.firstName || undefined,
            lastName: form.lastName || undefined,
            phone: form.phone || undefined,
            role: form.role,
          },
          tenant.slug,
        );
      } else if (modal.type === 'edit') {
        await usersApi.update(
          modal.user.membershipId,
          {
            firstName: form.firstName || null,
            lastName: form.lastName || null,
            phone: form.phone || null,
            role: modal.user.role === 'CUSTOMER' ? 'GYM_STAFF' : modal.user.role,
          },
          tenant.slug,
        );
      }
      setModal({ type: 'none' });
      await load();
      onChanged();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        msg ?? translate('pages.admin.admin_franchise_page.save_admin.set_error.error_user_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeAdmin() {
    if (modal.type !== 'remove') return;
    setSaving(true);
    setError(null);
    try {
      await usersApi.remove(modal.user.membershipId, tenant.slug);
      setModal({ type: 'none' });
      await load();
      onChanged();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(
        msg ?? translate('pages.admin.admin_franchise_page.remove_admin.set_error.error_user'),
      );
    } finally {
      setSaving(false);
    }
  }

  const showForm = modal.type === 'create' || modal.type === 'edit';

  return (
    <section
      className="af-card af-franchise-admins-panel"
      style={{
        width: 'min(980px, calc(100vw - 48px))',
        maxHeight: '88vh',
        overflowY: 'auto',
        padding: 24,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>
            {translate('pages.admin.admin_franchise_page.franchise_admins_panel.div.admin')}
          </div>
          <div className="af-mono" style={{ color: 'var(--af-dim)', fontSize: 11, marginTop: 3 }}>
            {tenant.name} {'·'} {tenant.slug}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="af-outline-btn" style={{ padding: '8px 14px' }} onClick={onClose}>
            {translate(
              'pages.admin.admin_franchise_page.franchise_admins_panel.button_outline_btn.close',
            )}
          </button>
          <button
            className="af-primary-btn"
            style={{ padding: '8px 14px', fontSize: 13 }}
            onClick={openCreate}
          >
            {translate(
              'pages.admin.admin_franchise_page.franchise_admins_panel.button_primary_btn.user_add',
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            padding: 20,
            color: 'var(--af-muted)',
          }}
        >
          <Spinner />{' '}
          {translate('pages.admin.admin_franchise_page.franchise_admins_panel.div.loading')}
        </div>
      ) : admins.length === 0 ? (
        <div style={{ padding: '28px 12px', textAlign: 'center', color: 'var(--af-muted)' }}>
          {translate('pages.admin.admin_franchise_page.franchise_admins_panel.div.tenant')}
        </div>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
                {[
                  translate('pages.admin.admin_franchise_page.franchise_admins_panel.tr.admin'),
                  translate('pages.admin.admin_franchise_page.franchise_admins_panel.tr.role'),
                  translate('pages.admin.admin_franchise_page.franchise_admins_panel.tr.phone'),
                  translate('pages.admin.admin_franchise_page.franchise_admins_panel.tr.booking'),
                  translate('pages.admin.admin_franchise_page.franchise_admins_panel.tr.status'),
                  '',
                ].map((heading) => (
                  <th
                    key={heading}
                    style={{
                      padding: '12px 14px',
                      textAlign: 'left',
                      fontSize: 10,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: 'var(--af-muted)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {admins.map((admin, index) => {
                const fullName = [admin.account.firstName, admin.account.lastName]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <tr
                    key={admin.membershipId}
                    style={{
                      borderBottom:
                        index < admins.length - 1 ? '1px solid var(--af-border)' : 'none',
                    }}
                  >
                    <td style={{ padding: '14px' }}>
                      <div style={{ fontWeight: 800 }}>{fullName || admin.account.email}</div>
                      <div
                        className="af-mono"
                        style={{ color: 'var(--af-dim)', fontSize: 11, marginTop: 2 }}
                      >
                        {admin.account.email}
                      </div>
                    </td>
                    <td style={{ padding: '14px' }}>
                      <Tag
                        color={
                          admin.role === 'OWNER_ADMIN' || admin.role === 'PLATFORM_ADMIN'
                            ? 'var(--af-accent)'
                            : 'var(--af-muted)'
                        }
                        bg={
                          admin.role === 'OWNER_ADMIN' || admin.role === 'PLATFORM_ADMIN'
                            ? 'var(--af-accent-soft)'
                            : 'rgba(255,255,255,0.06)'
                        }
                      >
                        {admin.role === 'CUSTOMER'
                          ? translate(
                              'pages.admin.admin_franchise_page.franchise_admins_panel.tag.user',
                            )
                          : tenantRoleLabels[admin.role]}
                      </Tag>
                    </td>
                    <td style={{ padding: '14px', color: 'var(--af-muted)' }}>
                      {admin.account.phone ?? '-'}
                    </td>
                    <td style={{ padding: '14px', color: 'var(--af-muted)' }}>
                      {admin.bookingsCount}
                    </td>
                    <td style={{ padding: '14px' }}>
                      {admin.account.isActive ? (
                        <Tag color="var(--af-success)" bg="rgba(74,222,128,0.1)">
                          {translate(
                            'pages.admin.admin_franchise_page.franchise_admins_panel.tag.active',
                          )}
                        </Tag>
                      ) : (
                        <Tag color="var(--af-danger)" bg="rgba(232,66,26,0.1)">
                          {translate(
                            'pages.admin.admin_franchise_page.franchise_admins_panel.tag.copy',
                          )}
                        </Tag>
                      )}
                    </td>
                    <td style={{ padding: '14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button
                          className="af-outline-btn"
                          style={{ padding: '6px 12px' }}
                          onClick={() => openEdit(admin)}
                        >
                          {translate(
                            'pages.admin.admin_franchise_page.franchise_admins_panel.button_outline_btn.edit',
                          )}
                        </button>
                        <button
                          className="af-outline-btn"
                          style={{ padding: '6px 12px', color: 'var(--af-danger)' }}
                          onClick={() => setModal({ type: 'remove', user: admin })}
                        >
                          {translate(
                            'pages.admin.admin_franchise_page.franchise_admins_panel.button_outline_btn.label',
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <Overlay onClose={() => setModal({ type: 'none' })}>
          <form
            className="af-card"
            style={{ width: '100%', maxWidth: 520, padding: 28 }}
            onSubmit={saveAdmin}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 800 }}>
              {modal.type === 'create'
                ? translate(
                    'pages.admin.admin_franchise_page.franchise_admins_panel.h2.user_tenant_add',
                  )
                : translate(
                    'pages.admin.admin_franchise_page.franchise_admins_panel.h2.user_tenant_edit',
                  )}
            </h2>
            <div style={{ display: 'grid', gap: 14 }}>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_franchise_page.franchise_admins_panel.label_label.email',
                )}

                <input
                  ref={emailRef}
                  className="af-input"
                  type="email"
                  value={form.email}
                  disabled={modal.type === 'edit'}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  required
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_franchise_page.franchise_admins_panel.label_label.name',
                  )}

                  <input
                    className="af-input"
                    value={form.firstName}
                    onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
                  />
                </label>
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_franchise_page.franchise_admins_panel.label_label.last_name',
                  )}

                  <input
                    className="af-input"
                    value={form.lastName}
                    onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
                  />
                </label>
              </div>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_franchise_page.franchise_admins_panel.label_label.phone',
                )}

                <PhoneInput
                  className="af-phone-input"
                  defaultCountry="CZ"
                  international
                  countryCallingCodeEditable
                  value={form.phone}
                  onChange={(value) => setForm((p) => ({ ...p, phone: value ?? '' }))}
                  placeholder={'+420 777 123 456'}
                />
              </label>
              {modal.type === 'create' && (
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_franchise_page.franchise_admins_panel.label_label.role',
                  )}

                  <CustomSelect
                    value={form.role}
                    onChange={(role) =>
                      setForm((current) => ({ ...current, role: role as UserRole }))
                    }
                    options={assignableRoles.map((role) => ({
                      value: role,
                      label: tenantRoleLabels[role],
                    }))}
                  />
                </label>
              )}
              {modal.type === 'create' && (
                <div
                  style={{
                    padding: '10px 12px',
                    border: '1px solid var(--af-border)',
                    background: 'var(--af-s2)',
                    borderRadius: 8,
                    color: 'var(--af-muted)',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {translate('pages.admin.admin_franchise_page.franchise_admins_panel.div.copy')}
                  {tenantRoleLabels[form.role]}{' '}
                  {translate(
                    'pages.admin.admin_franchise_page.franchise_admins_panel.div.tenant_2',
                  )}{' '}
                  {tenant.name}
                  {translate(
                    'pages.admin.admin_franchise_page.franchise_admins_panel.div.password',
                  )}
                </div>
              )}
              {error && <div className="af-error">{error}</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button
                className="af-outline-btn"
                type="button"
                style={{ padding: '10px 20px' }}
                onClick={() => setModal({ type: 'none' })}
                disabled={saving}
              >
                {translate(
                  'pages.admin.admin_franchise_page.franchise_admins_panel.button_outline_btn.cancel',
                )}
              </button>
              <button
                className="af-primary-btn"
                style={{ padding: '10px 22px', fontSize: 14 }}
                disabled={saving}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate(
                    'pages.admin.admin_franchise_page.franchise_admins_panel.button_primary_btn.save',
                  )
                )}
              </button>
            </div>
          </form>
        </Overlay>
      )}

      {modal.type === 'remove' && (
        <Overlay onClose={() => setModal({ type: 'none' })}>
          <div
            className="af-card"
            style={{ width: '100%', maxWidth: 420, padding: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800 }}>
              {translate('pages.admin.admin_franchise_page.franchise_admins_panel.h2.user')}
            </h2>
            <p
              style={{
                margin: '0 0 18px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {translate('pages.admin.admin_franchise_page.franchise_admins_panel.p.user')}{' '}
              <strong style={{ color: 'var(--af-text)' }}>{modal.user.account.email}</strong>
              {translate('pages.admin.admin_franchise_page.franchise_admins_panel.p.tenant')}
              {tenant.name}
              {'.'}
            </p>
            {error && (
              <div className="af-error" style={{ marginBottom: 14 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="af-outline-btn"
                style={{ padding: '10px 20px' }}
                onClick={() => setModal({ type: 'none' })}
                disabled={saving}
              >
                {translate(
                  'pages.admin.admin_franchise_page.franchise_admins_panel.button_outline_btn.back',
                )}
              </button>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid rgba(232,66,26,0.4)',
                  background: 'rgba(232,66,26,0.12)',
                  color: 'var(--af-danger)',
                  fontSize: 14,
                  fontWeight: 800,
                }}
                disabled={saving}
                onClick={() => void removeAdmin()}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate('pages.admin.admin_franchise_page.franchise_admins_panel.button.label')
                )}
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </section>
  );
}

function StatusLine({ label, ready }: { label: string; ready: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        background: 'var(--af-s2)',
        borderRadius: 8,
        border: '1px solid var(--af-border)',
      }}
    >
      <span style={{ color: 'var(--af-muted)', fontSize: 13 }}>{label}</span>
      {ready ? (
        <Tag color="var(--af-success)" bg="rgba(74,222,128,0.1)">
          {translate('pages.admin.admin_franchise_page.status_line.tag.copy')}
        </Tag>
      ) : (
        <Tag color="var(--af-peak)" bg="var(--af-peak-soft)">
          {translate('pages.admin.admin_franchise_page.status_line.tag.copy_2')}
        </Tag>
      )}
    </div>
  );
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}
